// Real-time monitoring features
// Background connectivity monitoring with alerts

export interface MonitorConfig {
  target: string;
  interval: number; // seconds
  latencyThreshold: number; // ms
  packetLossThreshold: number; // percentage
  notificationsEnabled: boolean;
}

export interface MonitorEvent {
  timestamp: number;
  type: 'success' | 'warning' | 'error';
  latency?: number;
  packetLoss?: number;
  bandwidth?: number;
  message: string;
}

export interface MonitorStats {
  startTime: number;
  totalTests: number;
  successfulTests: number;
  failedTests: number;
  avgLatency: number;
  minLatency: number;
  maxLatency: number;
  uptime: number; // percentage
  events: MonitorEvent[];
}

export class ConnectionMonitor {
  private config: MonitorConfig;
  private stats: MonitorStats;
  private intervalId: number | null = null;
  private onUpdate?: (stats: MonitorStats) => void;
  private onAlert?: (event: MonitorEvent) => void;
  private isRunning = false;

  constructor(
    config: MonitorConfig,
    onUpdate?: (stats: MonitorStats) => void,
    onAlert?: (event: MonitorEvent) => void
  ) {
    this.config = config;
    this.onUpdate = onUpdate;
    this.onAlert = onAlert;

    this.stats = {
      startTime: Date.now(),
      totalTests: 0,
      successfulTests: 0,
      failedTests: 0,
      avgLatency: 0,
      minLatency: Infinity,
      maxLatency: 0,
      uptime: 100,
      events: []
    };
  }

  start(): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.stats.startTime = Date.now();

    // Request notification permission if enabled
    if (this.config.notificationsEnabled && 'Notification' in window) {
      Notification.requestPermission();
    }

    // Run first test immediately
    this.runTest();

    // Schedule periodic tests
    this.intervalId = window.setInterval(() => {
      this.runTest();
    }, this.config.interval * 1000);
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
  }

  getStats(): MonitorStats {
    return { ...this.stats };
  }

  isActive(): boolean {
    return this.isRunning;
  }

  updateConfig(config: Partial<MonitorConfig>): void {
    this.config = { ...this.config, ...config };

    // Restart if running with new interval
    if (this.isRunning && config.interval) {
      this.stop();
      this.start();
    }
  }

  private async runTest(): Promise<void> {
    const start = performance.now();

    try {
      const response = await fetch(this.config.target, {
        method: 'HEAD',
        cache: 'no-store',
        signal: AbortSignal.timeout(10000)
      });

      const latency = performance.now() - start;

      this.stats.totalTests++;

      if (response.ok) {
        this.stats.successfulTests++;
        this.updateLatencyStats(latency);

        // Check thresholds
        if (latency > this.config.latencyThreshold) {
          this.addEvent({
            timestamp: Date.now(),
            type: 'warning',
            latency,
            message: `High latency detected: ${latency.toFixed(0)}ms (threshold: ${this.config.latencyThreshold}ms)`
          });
        } else {
          this.addEvent({
            timestamp: Date.now(),
            type: 'success',
            latency,
            message: `Connection OK (${latency.toFixed(0)}ms)`
          });
        }
      } else {
        this.stats.failedTests++;
        this.addEvent({
          timestamp: Date.now(),
          type: 'error',
          message: `HTTP error: ${response.status} ${response.statusText}`
        });
      }
    } catch (error: any) {
      this.stats.totalTests++;
      this.stats.failedTests++;

      this.addEvent({
        timestamp: Date.now(),
        type: 'error',
        message: `Connection failed: ${error.message}`
      });
    }

    // Calculate uptime
    this.stats.uptime = (this.stats.successfulTests / this.stats.totalTests) * 100;

    // Notify callback
    if (this.onUpdate) {
      this.onUpdate(this.getStats());
    }
  }

  private updateLatencyStats(latency: number): void {
    if (latency < this.stats.minLatency) {
      this.stats.minLatency = latency;
    }
    if (latency > this.stats.maxLatency) {
      this.stats.maxLatency = latency;
    }

    // Update rolling average
    const totalLatency = this.stats.avgLatency * (this.stats.successfulTests - 1);
    this.stats.avgLatency = (totalLatency + latency) / this.stats.successfulTests;
  }

  private addEvent(event: MonitorEvent): void {
    this.stats.events.unshift(event);

    // Keep only last 100 events
    if (this.stats.events.length > 100) {
      this.stats.events = this.stats.events.slice(0, 100);
    }

    // Trigger alert for warnings and errors
    if ((event.type === 'warning' || event.type === 'error') && this.onAlert) {
      this.onAlert(event);

      // Show desktop notification if enabled
      if (this.config.notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Connectivity Alert', {
          body: event.message,
          icon: '/logos/logo_square.png',
          badge: '/logos/logo_square.png',
          tag: 'connectivity-monitor'
        });
      }
    }
  }

  clearEvents(): void {
    this.stats.events = [];
  }

  reset(): void {
    this.stats = {
      startTime: Date.now(),
      totalTests: 0,
      successfulTests: 0,
      failedTests: 0,
      avgLatency: 0,
      minLatency: Infinity,
      maxLatency: 0,
      uptime: 100,
      events: []
    };

    if (this.onUpdate) {
      this.onUpdate(this.getStats());
    }
  }

  exportStats(): string {
    const duration = Date.now() - this.stats.startTime;
    const durationMinutes = Math.floor(duration / 60000);

    return `# Connection Monitor Report

**Target**: ${this.config.target}
**Duration**: ${durationMinutes} minutes
**Interval**: ${this.config.interval} seconds

## Statistics

- **Total Tests**: ${this.stats.totalTests}
- **Successful**: ${this.stats.successfulTests}
- **Failed**: ${this.stats.failedTests}
- **Uptime**: ${this.stats.uptime.toFixed(2)}%

## Latency

- **Average**: ${this.stats.avgLatency.toFixed(2)} ms
- **Minimum**: ${this.stats.minLatency === Infinity ? 'N/A' : this.stats.minLatency.toFixed(2) + ' ms'}
- **Maximum**: ${this.stats.maxLatency.toFixed(2)} ms

## Recent Events

${this.stats.events.slice(0, 20).map(e => {
  const time = new Date(e.timestamp).toLocaleTimeString();
  const icon = e.type === 'error' ? '❌' : e.type === 'warning' ? '⚠️' : '✅';
  return `- ${icon} [${time}] ${e.message}`;
}).join('\n')}
`;
  }
}

// SLA Tracking
export interface SLAConfig {
  targetUptime: number; // percentage
  targetLatency: number; // ms
  measurementPeriod: 'hour' | 'day' | 'week' | 'month';
}

export interface SLAStatus {
  currentUptime: number;
  targetUptime: number;
  uptimeMet: boolean;
  avgLatency: number;
  targetLatency: number;
  latencyMet: boolean;
  overallSLAMet: boolean;
  timeRemaining: number; // seconds until period end
  credits?: number; // SLA credits earned if violated
}

export function calculateSLAStatus(
  stats: MonitorStats,
  config: SLAConfig
): SLAStatus {
  const uptimeMet = stats.uptime >= config.targetUptime;
  const latencyMet = stats.avgLatency <= config.targetLatency;

  // Calculate time remaining in period
  const periodMs = {
    hour: 3600000,
    day: 86400000,
    week: 604800000,
    month: 2592000000
  }[config.measurementPeriod];

  const elapsed = Date.now() - stats.startTime;
  const timeRemaining = Math.max(0, periodMs - elapsed) / 1000;

  // Calculate SLA credits (simplified)
  let credits = 0;
  if (!uptimeMet) {
    const missedUptime = config.targetUptime - stats.uptime;
    credits = Math.floor(missedUptime * 10); // 10 credits per 1% missed
  }

  return {
    currentUptime: stats.uptime,
    targetUptime: config.targetUptime,
    uptimeMet,
    avgLatency: stats.avgLatency,
    targetLatency: config.targetLatency,
    latencyMet,
    overallSLAMet: uptimeMet && latencyMet,
    timeRemaining,
    credits: credits > 0 ? credits : undefined
  };
}

// Alerting Rules Engine
export type AlertCondition =
  | { type: 'latency_above'; threshold: number }
  | { type: 'latency_below'; threshold: number }
  | { type: 'packet_loss_above'; threshold: number }
  | { type: 'uptime_below'; threshold: number }
  | { type: 'consecutive_failures'; count: number };

export interface AlertRule {
  id: string;
  name: string;
  condition: AlertCondition;
  enabled: boolean;
  cooldown: number; // seconds between alerts
  lastTriggered?: number;
}

export class AlertEngine {
  private rules: Map<string, AlertRule> = new Map();
  private onAlert?: (rule: AlertRule, message: string) => void;

  constructor(onAlert?: (rule: AlertRule, message: string) => void) {
    this.onAlert = onAlert;
  }

  addRule(rule: AlertRule): void {
    this.rules.set(rule.id, rule);
  }

  removeRule(id: string): void {
    this.rules.delete(id);
  }

  updateRule(id: string, updates: Partial<AlertRule>): void {
    const rule = this.rules.get(id);
    if (rule) {
      this.rules.set(id, { ...rule, ...updates });
    }
  }

  getRules(): AlertRule[] {
    return Array.from(this.rules.values());
  }

  evaluate(stats: MonitorStats, currentLatency?: number, currentPacketLoss?: number): void {
    const now = Date.now();

    for (const rule of this.rules.values()) {
      if (!rule.enabled) {
        continue;
      }

      // Check cooldown
      if (rule.lastTriggered && (now - rule.lastTriggered) < rule.cooldown * 1000) {
        continue;
      }

      let triggered = false;
      let message = '';

      switch (rule.condition.type) {
        case 'latency_above':
          if (currentLatency && currentLatency > rule.condition.threshold) {
            triggered = true;
            message = `Latency ${currentLatency.toFixed(0)}ms exceeds threshold ${rule.condition.threshold}ms`;
          }
          break;

        case 'latency_below':
          if (currentLatency && currentLatency < rule.condition.threshold) {
            triggered = true;
            message = `Latency ${currentLatency.toFixed(0)}ms below threshold ${rule.condition.threshold}ms`;
          }
          break;

        case 'packet_loss_above':
          if (currentPacketLoss !== undefined && currentPacketLoss > rule.condition.threshold) {
            triggered = true;
            message = `Packet loss ${currentPacketLoss.toFixed(1)}% exceeds threshold ${rule.condition.threshold}%`;
          }
          break;

        case 'uptime_below':
          if (stats.uptime < rule.condition.threshold) {
            triggered = true;
            message = `Uptime ${stats.uptime.toFixed(2)}% below threshold ${rule.condition.threshold}%`;
          }
          break;

        case 'consecutive_failures':
          // Check last N events for consecutive failures
          const recentEvents = stats.events.slice(0, rule.condition.count);
          const allFailures = recentEvents.every(e => e.type === 'error');
          if (recentEvents.length >= rule.condition.count && allFailures) {
            triggered = true;
            message = `${rule.condition.count} consecutive failures detected`;
          }
          break;
      }

      if (triggered && this.onAlert) {
        rule.lastTriggered = now;
        this.rules.set(rule.id, rule);
        this.onAlert(rule, message);
      }
    }
  }
}
